import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Warning } from "@phosphor-icons/react";

const FIELDS = [
  ["risk_per_trade", "Risk / Trade ($)"],
  ["daily_max_loss", "Daily Max Loss ($)"],
  ["tp_target_usd", "Take Profit Target ($)"],
  ["stop_loss_usd", "Stop Loss / Trade ($)"],
  ["max_hold_min", "Max Hold Time (min)"],
  ["min_volume_24h", "Min Volume 24h ($)"],
  ["max_token_age_hours", "Max Token Age (h)"],
  ["cooldown_min", "Cooldown After Callout (min)"],
  ["callout_window_min", "Callout Window (min)"],
];

export default function SettingsDialog({ open, onOpenChange, config, onSave }) {
  const [form, setForm] = useState(config || {});
  useEffect(() => { setForm(config || {}); }, [config, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const live = form.mode === "live";

  const save = () => {
    const patch = {};
    FIELDS.forEach(([k]) => { patch[k] = Number(form[k]); });
    patch.mode = form.mode;
    onSave(patch);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#09090b] border-[#27272a] rounded-none max-w-lg" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight">Risk Engine Settings</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between border border-[#27272a] p-3">
          <div>
            <p className="text-sm font-medium">Trading Mode</p>
            <p className="text-[11px] text-[#a1a1aa] font-mono">{live ? "LIVE — execution is SIMULATED (no wallet keys)" : "PAPER — safe simulation"}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono ${live ? "text-red-400" : "text-cyan-400"}`}>{live ? "LIVE" : "PAPER"}</span>
            <Switch data-testid="mode-switch" checked={live} onCheckedChange={(v) => set("mode", v ? "live" : "paper")} />
          </div>
        </div>
        {live && (
          <div className="flex items-start gap-2 text-[11px] text-yellow-400 font-mono border border-yellow-500/30 bg-yellow-500/5 p-2">
            <Warning size={14} className="mt-0.5 shrink-0" />
            On-chain signing/broadcast is not wired. LIVE mode still simulates fills on real market prices.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 max-h-[45vh] overflow-y-auto pr-1">
          {FIELDS.map(([k, label]) => (
            <div key={k}>
              <Label className="text-[10px] uppercase tracking-wide text-[#a1a1aa]">{label}</Label>
              <Input data-testid={`setting-${k}`} type="number" value={form[k] ?? ""}
                onChange={(e) => set(k, e.target.value)}
                className="mt-1 bg-[#18181b] border-[#27272a] rounded-none font-mono text-sm focus-visible:ring-cyan-500" />
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}
            className="rounded-none border-[#27272a] bg-transparent hover:bg-[#18181b]">Cancel</Button>
          <Button data-testid="save-settings-btn" onClick={save}
            className="rounded-none bg-cyan-500 hover:bg-cyan-400 text-black font-semibold active:scale-95">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
